import { useState } from 'react';
import { Search, Phone, Mail, MessageCircle, ChevronRight, HelpCircle, Book, AlertCircle, CreditCard, Package, MapPin, Shield } from 'lucide-react';

interface HelpCenterScreenProps {
  onBack: () => void;
  onStartChat: () => void;
}

export function HelpCenterScreen({ onBack, onStartChat }: HelpCenterScreenProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const categories = [
    {
      id: 'getting-started',
      name: 'Getting Started',
      icon: Book,
      color: 'blue',
      articles: [
        'How to accept my first delivery',
        'Understanding the app interface',
        'Setting up my profile',
        'Completing the training',
      ],
    },
    {
      id: 'deliveries',
      name: 'Deliveries',
      icon: Package,
      color: 'green',
      articles: [
        'How to pickup a package',
        'Proof of delivery requirements',
        'What to do if customer is unavailable',
        'Handling damaged packages',
        'Multiple pickup deliveries',
      ],
    },
    {
      id: 'navigation',
      name: 'Navigation',
      icon: MapPin,
      color: 'purple',
      articles: [
        'Using the navigation system',
        'Reporting wrong addresses',
        'Updating GPS location',
        'Offline maps',
      ],
    },
    {
      id: 'earnings',
      name: 'Earnings & Payments',
      icon: CreditCard,
      color: 'orange',
      articles: [
        'How earnings are calculated',
        'When do I get paid?',
        'Updating bank details',
        'Understanding bonuses',
        'Tax information',
      ],
    },
    {
      id: 'safety',
      name: 'Safety',
      icon: Shield,
      color: 'red',
      articles: [
        'Using the SOS button',
        'Reporting incidents',
        'Insurance coverage',
        'Safety guidelines',
        'Emergency contacts',
      ],
    },
    {
      id: 'account',
      name: 'Account Issues',
      icon: AlertCircle,
      color: 'yellow',
      articles: [
        'Reset my password',
        'Update profile information',
        'Change fleet type',
        'Account suspended - what to do',
        'Deactivating my account',
      ],
    },
  ];

  const quickActions = [
    {
      id: 'call',
      name: 'Call Support',
      description: '+234 123 456 7890',
      icon: Phone,
      action: () => window.open('tel:+2341234567890'),
    },
    {
      id: 'email',
      name: 'Email Us',
      description: 'riders@go7.ng',
      icon: Mail,
      action: () => window.open('mailto:riders@go7.ng'),
    },
    {
      id: 'chat',
      name: 'Live Chat',
      description: 'Average response: 2 min',
      icon: MessageCircle,
      action: onStartChat,
    },
  ];

  if (selectedCategory) {
    const category = categories.find(c => c.id === selectedCategory);
    if (!category) return null;

    const Icon = category.icon;
    const colorClasses = {
      blue: 'bg-blue-100 text-blue-600 border-blue-200',
      green: 'bg-green-100 text-green-600 border-green-200',
      purple: 'bg-purple-100 text-purple-600 border-purple-200',
      orange: 'bg-orange-100 text-orange-600 border-orange-200',
      red: 'bg-red-100 text-red-600 border-red-200',
      yellow: 'bg-yellow-100 text-yellow-600 border-yellow-200',
    };

    return (
      <div className="fixed inset-0 bg-white flex flex-col">
        <div className="p-8 pt-16">
          <button
            onClick={() => setSelectedCategory(null)}
            className="mb-6 text-[#E0001B] hover:underline"
          >
            ← Back to Help Center
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${colorClasses[category.color as keyof typeof colorClasses]}`}>
              <Icon className="w-6 h-6" />
            </div>
            <h1 className="text-[24px] font-medium">{category.name}</h1>
          </div>
        </div>

        <div className="flex-1 overflow-auto px-8 pb-8">
          <div className="space-y-3">
            {category.articles.map((article, index) => (
              <button
                key={index}
                className="w-full p-4 bg-white border border-gray-200 rounded-[12px] hover:border-[#E0001B] transition-colors flex items-center justify-between group"
              >
                <div className="flex items-center gap-3">
                  <HelpCircle className="w-5 h-5 text-[#6B6B6B] group-hover:text-[#E0001B] transition-colors" />
                  <span className="text-[14px] text-left">{article}</span>
                </div>
                <ChevronRight className="w-5 h-5 text-[#6B6B6B] group-hover:text-[#E0001B] transition-colors" />
              </button>
            ))}
          </div>

          <div className="mt-6 p-6 bg-gray-50 rounded-[12px]">
            <p className="text-[14px] text-[#6B6B6B] text-center mb-4">
              Can't find what you're looking for?
            </p>
            <button
              onClick={onStartChat}
              className="w-full h-12 rounded-[12px] bg-[#E0001B] text-white hover:bg-[#C00017] transition-colors flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-5 h-5" />
              Chat with Support
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-white flex flex-col">
      {/* Header */}
      <div className="p-8 pt-16 bg-gradient-to-b from-[#E0001B]/5 to-transparent">
        <button
          onClick={onBack}
          className="mb-6 text-[#E0001B] hover:underline"
        >
          ← Back
        </button>

        <h1 className="text-[28px] text-[#111111] mb-6">
          Help Center
        </h1>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B6B6B]" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search for help..."
            className="w-full h-14 pl-12 pr-4 rounded-[12px] bg-white border border-gray-200 focus:border-[#E0001B] focus:outline-none transition-colors"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-8 pb-8">
        {/* Quick Actions */}
        <div className="mb-6">
          <h2 className="text-[18px] font-medium mb-4">Contact Support</h2>
          <div className="space-y-3">
            {quickActions.map(action => {
              const Icon = action.icon;
              return (
                <button
                  key={action.id}
                  onClick={action.action}
                  className="w-full p-4 bg-white border border-gray-200 rounded-[12px] hover:border-[#E0001B] transition-colors flex items-center gap-4 group"
                >
                  <div className="w-12 h-12 rounded-full bg-[#E0001B]/10 flex items-center justify-center group-hover:bg-[#E0001B] transition-colors">
                    <Icon className="w-6 h-6 text-[#E0001B] group-hover:text-white transition-colors" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-[16px] font-medium mb-0.5">{action.name}</p>
                    <p className="text-[14px] text-[#6B6B6B]">{action.description}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-[#6B6B6B]" />
                </button>
              );
            })}
          </div>
        </div>

        {/* FAQ Categories */}
        <div>
          <h2 className="text-[18px] font-medium mb-4">Browse by Topic</h2>
          <div className="grid grid-cols-2 gap-3">
            {categories.map(category => {
              const Icon = category.icon;
              const colorClasses = {
                blue: 'bg-blue-50 border-blue-100 hover:border-blue-300',
                green: 'bg-green-50 border-green-100 hover:border-green-300',
                purple: 'bg-purple-50 border-purple-100 hover:border-purple-300',
                orange: 'bg-orange-50 border-orange-100 hover:border-orange-300',
                red: 'bg-red-50 border-red-100 hover:border-red-300',
                yellow: 'bg-yellow-50 border-yellow-100 hover:border-yellow-300',
              };
              const iconColorClasses = {
                blue: 'text-blue-600',
                green: 'text-green-600',
                purple: 'text-purple-600',
                orange: 'text-orange-600',
                red: 'text-red-600',
                yellow: 'text-yellow-600',
              };

              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`p-4 rounded-[12px] border-2 transition-colors ${colorClasses[category.color as keyof typeof colorClasses]}`}
                >
                  <Icon className={`w-8 h-8 mb-3 ${iconColorClasses[category.color as keyof typeof iconColorClasses]}`} />
                  <h3 className="text-[14px] font-medium mb-1">{category.name}</h3>
                  <p className="text-[12px] text-[#6B6B6B]">{category.articles.length} articles</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Support Hours */}
        <div className="mt-6 p-4 bg-gray-50 rounded-[12px]">
          <p className="text-[14px] text-[#6B6B6B] text-center">
            <strong>Support Hours:</strong> Monday - Sunday, 6:00 AM - 10:00 PM
          </p>
        </div>
      </div>
    </div>
  );
}
